import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, Select } from 'common/components';
import { createWorkflowTrunk } from 'services/api/PhoneNumberAPI';
import { Tenant } from 'types/accessManager.type';
import styles from '../PhoneNumberManager.module.scss';

interface CreateTrunkForm {
    tenantId: string;
    workflowId: string;
}

interface CreateWorkflowTrunkTabProps {
    tenants: Tenant[];
    isAccessManagerLoading: boolean;
    showNotification: (message: string, type: 'success' | 'error') => void;
}

const CreateWorkflowTrunkTab: React.FC<CreateWorkflowTrunkTabProps> = ({
    tenants,
    isAccessManagerLoading,
    showNotification,
}) => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const {
        handleSubmit: handleSubmitCreateTrunk,
        control: controlCreateTrunk,
        formState: { errors: errorsCreateTrunk },
        reset: resetCreateTrunk,
        watch: watchCreateTrunk,
    } = useForm<CreateTrunkForm>({
        defaultValues: {
            tenantId: '',
            workflowId: '',
        },
    });

    const selectedCreateTrunkTenantId = watchCreateTrunk('tenantId');

    // Get workflows for create trunk tenant
    const selectedCreateTrunkTenant = tenants.find((tenant) => tenant.id === selectedCreateTrunkTenantId);
    const createTrunkWorkflowOptions = selectedCreateTrunkTenant?.workflows || [];

    const onCreateTrunkSubmit = async (data: CreateTrunkForm) => {
        setIsSubmitting(true);

        try {
            const response = await createWorkflowTrunk({
                tenantId: data.tenantId,
                workflowId: data.workflowId,
            });

            showNotification(
                response.message || `Successfully created workflow trunk. Twilio SID: ${response.twilioTrunkSid}`,
                'success'
            );
            resetCreateTrunk();
        } catch (error: any) {
            console.error('Error creating workflow trunk:', error);
            const errorMessage =
                error.response?.data?.message || error.message || 'Failed to create workflow trunk. Please try again.';
            showNotification(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.createTrunkContainer}>
            <div className={styles.warningNote}>
                <strong>⚠️ Important:</strong> Remember YOU MUST enable Symmetric RTP in Twilio manually for a SIP trunk
                once created.
            </div>
            <form onSubmit={handleSubmitCreateTrunk(onCreateTrunkSubmit)} className={styles.form}>
                <div className={styles.formSection}>
                    <h2 className={styles.sectionTitle}>Create Workflow Trunk</h2>
                    <p className={styles.description}>
                        Create a new SIP trunk for a specific workflow. This will generate Twilio and LiveKit trunk SIDs
                        for the selected workflow.
                    </p>

                    <div className={styles.formGroup}>
                        <Controller
                            name='tenantId'
                            control={controlCreateTrunk}
                            rules={{ required: 'Tenant is required' }}
                            render={({ field }) => (
                                <Select
                                    label='Tenant *'
                                    options={[
                                        { value: '', label: 'Select a Tenant', disabled: true },
                                        ...(tenants || []).map((tenant) => ({
                                            value: tenant.id,
                                            label: tenant.name,
                                        })),
                                    ]}
                                    {...field}
                                    disabled={isAccessManagerLoading}
                                />
                            )}
                        />
                        {errorsCreateTrunk.tenantId && (
                            <span className={styles.errorText}>{errorsCreateTrunk.tenantId.message}</span>
                        )}
                    </div>

                    <div className={styles.formGroup}>
                        <Controller
                            name='workflowId'
                            control={controlCreateTrunk}
                            rules={{ required: 'Workflow is required' }}
                            render={({ field }) => (
                                <Select
                                    label='Workflow *'
                                    options={[
                                        { value: '', label: 'Select a Workflow', disabled: true },
                                        ...createTrunkWorkflowOptions.map((workflow) => ({
                                            value: workflow.id,
                                            label: workflow.title,
                                        })),
                                    ]}
                                    {...field}
                                    disabled={!selectedCreateTrunkTenantId || isAccessManagerLoading}
                                />
                            )}
                        />
                        {errorsCreateTrunk.workflowId && (
                            <span className={styles.errorText}>{errorsCreateTrunk.workflowId.message}</span>
                        )}
                    </div>

                    <div className={styles.buttonContainer}>
                        <Button type='submit' variant='primary' disabled={isSubmitting || isAccessManagerLoading}>
                            {isSubmitting ? 'Creating Trunk...' : 'Create Workflow Trunk'}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CreateWorkflowTrunkTab;
